var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import { classNames } from './utils';
export const buttonVariants = cva('inline-flex w-fit items-center justify-center gap-2 whitespace-nowrap cursor-pointer rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,opacity] disabled:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-base-200', {
    variants: {
        variant: {
            'default': 'bg-brand-500 text-white hover:bg-brand-600 dark:hover:bg-brand-400 active:bg-brand-500 active:ring-2 active:ring-base-200 disabled:bg-base-300 dark:disabled:text-base-500',
            'outline': 'bg-card border-base-200 dark:border-base-300 hover:bg-base-50 active:bg-card border shadow-xs active:ring-2 active:ring-base-200 disabled:border-base-100 dark:disabled:border-base-200 disabled:text-base-400 dark:disabled:text-text-muted',
            'ghost': 'hover:bg-base-100 hover:text-accent-foreground dark:hover:bg-accent/50',
            'link': 'text-primary underline-offset-4 hover:underline',
            'link-muted': 'text-text-muted underline-offset-4 hover:text-text-secondary hover:underline',
        },
        color: {
            default: '',
            primary: '',
            positive: '',
            negative: '',
            white: '',
        },
        size: {
            'default': 'px-4 rounded-lg text-[15px] py-2 has-[>svg]:px-3',
            'xs': 'h-7 rounded-lg gap-1.5 px-2 has-[>svg]:px-2',
            'sm': 'h-8 rounded-lg gap-1.5 px-2.5 has-[>svg]:px-2.5',
            'md': 'h-9 rounded-lg gap-1.5 px-2.5 has-[>svg]:px-2.5',
            'lg': 'h-11 rounded-lg text-[17px] px-5 has-[>svg]:px-4',
            'xl': 'h-10 rounded-2lg gap-2 px-4 text-[15px] has-[>svg]:px-3',
            'micro': 'h-6 rounded-lg gap-1 px-1.5 has-[>svg]:px-1.5',
            'inline': 'h-auto rounded-sm gap-1 p-0 text-xs leading-4',
            'icon-xs': 'size-5 rounded-lg',
            'icon-sm': 'size-6 rounded-lg',
            'icon': 'size-7 rounded-lg',
            'icon-2lg': 'size-7 rounded-2lg',
            'icon-lg': 'size-8 rounded-lg',
            'icon-xl': 'size-9 rounded-lg',
            'icon-2xl': 'size-10 rounded-2lg',
            'responsive': 'rounded-lg px-4 py-2 text-[15px] max-lg:size-7! max-lg:p-0!',
            'responsive-md': 'h-9 rounded-lg gap-1.5 px-2.5 text-sm has-[>svg]:px-2.5 max-lg:size-7! max-lg:p-0!',
            'responsive-sm': 'h-8 rounded-lg gap-1.5 px-2.5 text-sm has-[>svg]:px-2.5 max-lg:size-7! max-lg:p-0!',
            'responsive-icon-lg': 'size-8 rounded-lg max-lg:size-7!',
            'responsive-icon-xl': 'size-9 rounded-lg max-lg:size-8!',
        },
    },
    compoundVariants: [
        // Default variant with different colors
        {
            variant: 'default',
            color: 'default',
            className: 'bg-brand-500 text-white hover:bg-brand-600 dark:hover:bg-brand-400 active:bg-brand-500 focus-visible:ring-brand-200',
        },
        {
            variant: 'default',
            color: 'primary',
            className: 'bg-brand-500 text-white hover:bg-brand-600 dark:hover:bg-brand-400 active:bg-brand-500 focus-visible:ring-brand-200',
        },
        {
            variant: 'default',
            color: 'positive',
            className: 'bg-emerald-600 text-white hover:bg-emerald-500 dark:hover:bg-emerald-500 active:bg-emerald-600 focus-visible:ring-emerald-200',
        },
        {
            variant: 'default',
            color: 'negative',
            className: 'bg-red-600 text-white hover:bg-red-500 dark:hover:bg-red-500 active:bg-red-600 focus-visible:ring-red-200',
        },
        {
            variant: 'default',
            color: 'white',
            className: 'bg-card text-base-900 hover:bg-base-100 dark:hover:bg-base-200 active:bg-card focus-visible:ring-base-200 disabled:bg-card disabled:text-base-400 dark:disabled:text-text-muted',
        },
        // Outline variant with different colors
        {
            variant: 'outline',
            color: 'default',
            className: 'border-base-200 dark:border-base-300 text-base-700 hover:bg-base-50 focus-visible:ring-base-200 disabled:border-base-100 dark:disabled:border-base-200 disabled:text-base-400 dark:disabled:text-text-muted',
        },
        {
            variant: 'outline',
            color: 'primary',
            className: 'border-brand-500 text-brand-600 hover:bg-brand-50 focus-visible:ring-brand-200 disabled:border-brand-200 disabled:text-brand-200',
        },
        {
            variant: 'outline',
            color: 'positive',
            className: 'border-emerald-600 text-emerald-600 hover:bg-emerald-50 focus-visible:ring-emerald-200 disabled:border-emerald-200 disabled:text-emerald-200 dark:hover:bg-success-50 dark:disabled:border-success-200 dark:disabled:text-success-200',
        },
        {
            variant: 'outline',
            color: 'negative',
            className: 'border-red-500 text-red-500 hover:bg-red-50 focus-visible:ring-red-200 disabled:border-red-200 disabled:text-red-200 dark:hover:bg-error-50 dark:disabled:border-error-200 dark:disabled:text-error-200',
        },
        {
            variant: 'outline',
            color: 'white',
            className: 'border-base-200 dark:border-base-300 text-base-900 hover:bg-base-100 dark:hover:bg-base-200 focus-visible:ring-base-200 disabled:border-base-100 dark:disabled:border-base-200 disabled:text-base-400 dark:disabled:text-text-muted',
        },
        // Ghost variant with different colors
        {
            variant: 'ghost',
            color: 'default',
            className: 'text-base-900 hover:bg-base-100 disabled:text-base-300',
        },
        {
            variant: 'ghost',
            color: 'primary',
            className: 'text-brand-600 hover:bg-brand-50 disabled:text-brand-300 dark:hover:bg-brand-50',
        },
        {
            variant: 'ghost',
            color: 'positive',
            className: 'text-emerald-600 hover:bg-emerald-50 disabled:text-emerald-300 dark:text-success-600 dark:hover:bg-success-50 dark:disabled:text-success-300',
        },
        {
            variant: 'ghost',
            color: 'negative',
            className: 'text-red-600 hover:bg-red-50 disabled:text-red-300 dark:text-error-600 dark:hover:bg-error-50 dark:disabled:text-error-300',
        },
        {
            variant: 'ghost',
            color: 'white',
            className: 'text-white hover:bg-base-500 disabled:text-base-300',
        },
    ],
    defaultVariants: {
        variant: 'default',
        color: 'default',
        size: 'default',
    },
});
export function ButtonLabel(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx("span", Object.assign({ className: classNames('hidden lg:inline', className) }, props)));
}
function Button(_a) {
    var { className, variant, color, size, asChild = false, loading = false, children, disabled } = _a, props = __rest(_a, ["className", "variant", "color", "size", "asChild", "loading", "children", "disabled"]);
    const Comp = asChild ? Slot : 'button';
    return (_jsxs(Comp, Object.assign({ "data-slot": "button", "data-variant": variant !== null && variant !== void 0 ? variant : 'default', className: classNames(buttonVariants({ variant, color, size, className })), disabled: disabled || loading }, props, { children: [loading && _jsx(Loader2Icon, { className: "animate-spin" }), _jsx(Slottable, { children: children })] })));
}
export default Button;
